import { useMemo, useRef, useState } from 'react';
import { ControllerRenderProps } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

export function UploadFileForm({
  label,
  description,
  btnLabel = 'btn.choose-file',
  accept,
  multiple,
  onUpload,
  loading = false,
  field,
}: {
  label: string;
  description?: string;
  btnLabel?: string;
  accept: string;
  multiple?: boolean;
  onUpload: ({ files }: { files: FileList }) => boolean | undefined;
  loading?: boolean;
  field?: ControllerRenderProps;
}) {
  const { t } = useTranslation();

  const viewModel = (() => {
    const inputRef = useRef<HTMLInputElement>(null);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);

    const btnOrFilenameLabel = useMemo(() => {
      if (selectedFile) {
        return selectedFile.name;
      }
      return t(btnLabel);
    }, [selectedFile]);

    return {
      inputRef,
      api: {
        upload: ({ files }: { files: FileList }) => {
          const shouldResetSelectedFile = onUpload({ files });
          if (shouldResetSelectedFile) {
            setSelectedFile(null);
          }
        },
        btnClick: (event: any) => {
          event.preventDefault();
          inputRef.current?.click();
        },
        onFilesChange: ({ files }: { files: FileList }) => {
          if (files.length === 1) {
            setSelectedFile(files[0]!);
          }

          viewModel.api.upload({
            files,
          });
        },
      },
      btnOrFilenameLabel,
    };
  })();

  return (
    <div className="flex flex-col gap-2">
      <div className="font-semibold text-sm">{t(label)}</div>

      <div>
        <Button
          className="w-full"
          onClick={viewModel.api.btnClick}
          loading={loading}
        >
          {viewModel.btnOrFilenameLabel}
        </Button>

        <div className="hidden">
          <Input
            ref={viewModel.inputRef}
            type="file"
            accept={accept}
            multiple={multiple}
            onChange={(event) => {
              const files = event.target.files!;
              viewModel.api.onFilesChange({ files });

              event.target.value = '';
            }}
            {...field}
          />
        </div>
      </div>

      {description && (
        <span className="text-muted-foreground text-sm">{t(description)}</span>
      )}
    </div>
  );
}
